import { filterOutFnfComponents, filterOutRecurringDeductions } from 'components/SalaryComponents/utils';
import { gql } from 'graphql-request';
import { GetFnfResponseSchemaContract } from 'schemas/GetFnfResponseSchema';
import { graphQLApi } from 'utils/Api';
import { generateUserId } from 'utils/commons';
import { PayrollGetFnfDataV2Response, PayrollGetFnfDataVariables } from '../types';
import { mapAdditions, mapDeductions, mapLopDeduction } from './getFnfDetails';

const getFnfDetailsQuery = gql`
  query PayrollGetFnfDataV2($userId: String!, $deactivateDate: String!, $payType: PayTypeFilter) {
    payrollGetFnfDataV2(userId: $userId, deactivateDate: $deactivateDate, payType: $payType) {
      data {
        additions {
          id
          componentId
          componentName
          userId
          amount
          effectiveFrom
          effectiveTo
          type
          additionalSdiData
          componentDetails {
            id
            name
            description
            defaultFormula
            componentType
            category
            settings {
              behaviour {
                pfBehaviour {
                  includeInPfWages
                }
                ptBehaviour {
                  includeInPtWages
                }
                esiBehaviour {
                  includeInEsiWages
                }
                lwfBehaviour {
                  includeInLwfWages
                }
                taxBehaviour {
                  taxExemptionSection
                  taxDeductionBehaviour
                  taxExemptInNewRegime
                  taxExemptInOldRegime
                }
                arrearBehaviour {
                  considerInArrear
                }
                prorationBehaviour {
                  supportProration
                }
                displayBehaviour {
                  showZero
                  visibleToEmployee
                  displayName
                }
              }
              earningDetails {
                payType
              }
            }
            isPredefined
            isActive
            source
          }
        }
        deductions {
          id
          componentId
          componentName
          userId
          amount
          effectiveFrom
          effectiveTo
          type
          additionalSdiData
          componentDetails {
            id
            name
            description
            defaultFormula
            componentType
            category
            settings {
              behaviour {
                pfBehaviour {
                  includeInPfWages
                }
                ptBehaviour {
                  includeInPtWages
                }
                esiBehaviour {
                  includeInEsiWages
                }
                lwfBehaviour {
                  includeInLwfWages
                }
                taxBehaviour {
                  taxExemptionSection
                  taxDeductionBehaviour
                  taxExemptInNewRegime
                  taxExemptInOldRegime
                }
                arrearBehaviour {
                  considerInArrear
                }
                prorationBehaviour {
                  supportProration
                }
                displayBehaviour {
                  showZero
                  visibleToEmployee
                  displayName
                }
              }
              deductionDetails {
                deductionType
                deductFrom
              }
            }
            isPredefined
            isActive
            source
          }
        }
        lopData {
          id
          peopleId
          organizationId
          payrollMonth
          lopDays
          source
          createdAt
          updatedAt
          deletedAt
        }
        userData {
          id
          name
          organizationId
          peopleId
          hireDate
          title
          deactivateDate
          location
          employeeId
          optionalUserData
          department
          idPrefix
          uiPreferences
          reasonForLeaving
        }
        userStatuses {
          usersStatus {
            userId
            status
          }
          isOrgFinal
        }
        fnfConfig {
          gratuityApplicable
          gratuityAmount
          leaveEncashmentDays
          leaveEncashmentAmount
          leaveTypes {
            type
            balance
            total
          }
        }
      }
      code
      success
      message
    }
  }
`;

export const transformToFnfResponseSchema = (
  response: PayrollGetFnfDataV2Response,
  queryVariables: PayrollGetFnfDataVariables,
): GetFnfResponseSchemaContract => {
  const {
    userData,
    additions: internalAdditions,
    deductions: internalDeductions,
    lopData,
    userStatuses,
    fnfConfig = {
      gratuityApplicable: false,
      gratuityAmount: null,
      leaveEncashmentDays: null,
      leaveEncashmentAmount: null,
      leaveTypes: [],
    },
  } = response.payrollGetFnfDataV2.data;

  const additions = filterOutFnfComponents(internalAdditions);
  const deductions = filterOutFnfComponents(internalDeductions);
  const nonLopDeductions = filterOutRecurringDeductions(deductions);

  const userId = generateUserId(queryVariables.userId);
  const userStatus =
    userStatuses.usersStatus.find((status) => status.userId === userId)?.status || 'none';

  const payroll_status = {
    is_finalized: userStatus === 'finalized',
    is_skipped: userStatus === 'skipped',
    is_paid: userStatus === 'executed',
    is_finalization_in_progress: userStatus === 'created',
  };

  const is_fnf_processed = payroll_status.is_paid;

  return {
    is_dismissal_synced_by_hrms: false,
    is_fnf_processed,
    payroll_id: null,
    is_only_admin: false,
    show_bonus_banner: false,
    reason_for_leaving: userData.reasonForLeaving as string,
    loan_amount_required_for_full_recovery: null,
    deactivate_date: userData.deactivateDate as string,
    date_of_joining: userData.hireDate as string,
    net_pay_on_hold_from: null,
    personal_email_address: userData.optionalUserData?.personalEmailAddress as string,
    has_active_insurance: false,
    employee_name: userData.name as string,
    employee_id: userData.employeeId,
    employee_department: userData.department,
    employee_title: userData.title,
    leave_types: fnfConfig.leaveTypes ?? null,
    salary_advance: null,
    payroll_status,
    additions: mapAdditions(additions) || null,
    deductions: {
      non_lop_deductions: mapDeductions(nonLopDeductions) || null,
      lop_deduction: mapLopDeduction(lopData, queryVariables.userId),
      total_deduction: 0, // This needs to be calculated based on all deductions
    },
    leave_encashments: {
      leave_days_encashed: fnfConfig.leaveEncashmentDays!,
      total_encashment: fnfConfig.leaveEncashmentAmount
        ? parseInt(fnfConfig.leaveEncashmentAmount)
        : null,
    },
    gratuity: {
      gratuity_applicable: fnfConfig.gratuityApplicable,
      gratuity_amount: fnfConfig.gratuityAmount ? parseInt(fnfConfig.gratuityAmount) : null,
    },
  };
};

export const getFnfDetailsV2 = async (queryVariables: PayrollGetFnfDataVariables) => {
  try {
    const response = await graphQLApi<PayrollGetFnfDataV2Response, PayrollGetFnfDataVariables>({
      query: getFnfDetailsQuery,
      queryVariables,
    });

    return transformToFnfResponseSchema(response, queryVariables);
  } catch (error: unknown) {
    throw error;
  }
};
